import { useGlobalConfig } from "@tlc/ls2pac/components/GlobalConfig";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import { HostSystemItemView } from "@tlc/ls2pac/dto/HostSystemItemView";
import React from "react";
import { HoldStatus } from "./HoldStatus";
import { HoldPickupByDate, PhysicalHoldPickupInfo } from "./HoldPickupInfo";
import { CancelHoldButton } from "../CancelHoldButton";
import { Box, Button, Checkbox, Chip, Stack, Typography } from "@mui/material";
import { LSResultItemLayout } from "../../search/ls/LSResultItem/LSResultItemLayout";
import { LSResultItemTitle } from "../../search/ls/LSResultItem/LSResultItemTitle";
import { LSResultItemBookjacket } from "../../search/ls/LSResultItem/LSResultItemBookjacket";
import { LSResultItemAuthor } from "../../search/ls/LSResultItem/LSResultItemAuthor";
import { RatingHighContrast } from "@tlc/ls2pac/components/rate-and-review/RatingHighContrast";
import { LSSerialIssueInformation } from "../LSSerialIssueInformation";
import { LSResultItemReadingLevelDisplay } from "../../search/ls/LSResultItem/LSResultItemReadingLevelDisplay";
import { RateAndReview } from "@tlc/ls2pac/components/rate-and-review/RateAndReview";
import { ResourceSeriesLink } from "../../search/ls/ResourceSeriesLink";
import { GenreDisplay } from "../GenreDisplay";
import { useHoldsSelection } from "./HoldsSelectionContext";
import { DownloadableHoldCheckoutButton } from "../../resource/downloadable/CheckoutButton";

function isHoldStatusInTransit(status: string) {
  const InTransitHoldStatus = ["T", "HR", "HF"];
  return InTransitHoldStatus.includes(status);
}

function isHoldStatusArrived(status: string) {
  const ArrivedHoldStatus = "AR";
  return status === ArrivedHoldStatus;
}

export function canCancelHold(
  hold: HostSystemItemView,
  allowBorrowersToCancelArrivedHolds: boolean,
) {
  return (
    !isHoldStatusInTransit(hold.status) &&
    (!isHoldStatusArrived(hold.status) || allowBorrowersToCancelArrivedHolds)
  );
}

export function canReactivateOrSuspendHold(hold: HostSystemItemView) {
  return (
    !hold.downloadable &&
    !isHoldStatusInTransit(hold.status) &&
    !isHoldStatusArrived(hold.status)
  );
}

export const HoldsRowItem: React.FC<{
  id: string;
  hold: HostSystemItemView;
  isStudent: boolean;
  onClickSuspend: () => void;
  onClickCancel: () => void;
  onClickReactivate: () => void;
}> = ({
  id,
  hold,
  isStudent,
  onClickSuspend,
  onClickCancel,
  onClickReactivate,
}) => {
  const { allowBorrowersToCancelArrivedHolds } = useGlobalConfig();
  const { translate } = useLanguage();
  const { isSelected, handleItemChange } = useHoldsSelection();

  const displayCancel = canCancelHold(hold, allowBorrowersToCancelArrivedHolds);
  const displayReactivateOrSuspend = canReactivateOrSuspendHold(hold);
  const displayReactivate = displayReactivateOrSuspend && hold.suspendUntilDate;
  const displaySuspend = displayReactivateOrSuspend && !hold.suspendUntilDate;
  const displayHoldActions = displayCancel || displayReactivateOrSuspend;
  const { resource, title, author, enumeration, chronology } = hold;

  return (
    <>
      <LSResultItemLayout
        hideCheckbox={isStudent}
        titleComponent={
          resource ? (
            <LSResultItemTitle resource={resource} />
          ) : (
            <Typography component="h3" variant="h6" flex={1}>
              {title}
            </Typography>
          )
        }
        bookjacketComponent={
          <LSResultItemBookjacket
            {...(hold.resource
              ? { resource: hold.resource }
              : { title: hold.title })}
          />
        }
        checkboxComponent={
          !isStudent && (
            <Checkbox
              color="primary"
              checked={isSelected(hold)}
              onChange={(e, checked) => handleItemChange(hold, checked)}
              slotProps={{ input: { "aria-label": translate("select") } }}
            />
          )
        }
        actionsComponent={
          <>
            {displayHoldActions && !isStudent && (
              <Box>
                {displayCancel && <CancelHoldButton onClick={onClickCancel} />}
                {displayCancel && displayReactivateOrSuspend && (
                  <> {translate("or")} </>
                )}
                {displayReactivate && (
                  <Button onClick={onClickReactivate}>
                    {translate("reactivate")}
                  </Button>
                )}
                {displaySuspend && (
                  <Button onClick={onClickSuspend}>
                    {translate("suspend")}
                  </Button>
                )}
              </Box>
            )}
          </>
        }
        authorComponent={
          <LSResultItemAuthor shortAuthor={resource?.shortAuthor || author} />
        }
      >
        {/* Format label and rating */}
        <Box display="flex" alignItems="center" gap={2}>
          {resource?.format && (
            <Box>
              <Chip variant="outlined" label={resource?.format} />
            </Box>
          )}
          {resource &&
            (isStudent ? (
              <Box display="flex" alignItems="center" gap={2}>
                <RatingHighContrast
                  value={resource.ratingInformation?.average}
                  readOnly
                  precision={0.5}
                />
              </Box>
            ) : (
              <Box display="flex" alignItems="center" gap={2}>
                <RateAndReview material={resource} precision={0.5} />
              </Box>
            ))}
        </Box>
        {/* Series */}
        {resource?.mainSeries && (
          <Typography variant="subtitle2">
            {translate("series")}: <ResourceSeriesLink resource={resource} />
          </Typography>
        )}

        {/* Serial */}
        <LSSerialIssueInformation
          chronology={chronology}
          enumeration={enumeration}
        ></LSSerialIssueInformation>

        {/* Readling Level */}
        {resource && <LSResultItemReadingLevelDisplay resource={resource} />}

        {/* Genre */}
        <GenreDisplay item={hold} />

        <HoldStatus
          hold={hold}
          onClickSuspend={onClickSuspend}
          showChangeButton={!isStudent}
        ></HoldStatus>
        <PhysicalHoldPickupInfo hold={hold}></PhysicalHoldPickupInfo>
        {resource && hold.downloadable && hold.status == "AR" && !isStudent && (
          <Stack direction="row" alignItems="center" gap={1} mt={1}>
            <DownloadableHoldCheckoutButton
              hold={hold}
            ></DownloadableHoldCheckoutButton>{" "}
            {/* Overdrive and Baker and Taylor can have pickup by dates */}
            <HoldPickupByDate hold={hold} />
          </Stack>
        )}
      </LSResultItemLayout>
    </>
  );
};
